import {
  forwardRef,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ReactNode,
} from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { LoaderCircle } from "lucide-react";
import { cn } from "../../utils/classes";
import styles from "./action-button.module.css";

export type ActionVariant =
  | "primary"
  | "secondary"
  | "outline"
  | "ghost"
  | "link"
  | "destructive";

export type ActionButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ActionVariant;
  size?: "xs" | "sm" | "md" | "lg";
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  loading?: boolean;
};

/** Canonical action API. The legacy Button's variant names are unchanged. */
export const ActionButton = forwardRef<HTMLButtonElement, ActionButtonProps>(
  function ActionButton(
    {
      variant = "primary",
      size = "sm",
      leadingIcon,
      trailingIcon,
      loading = false,
      disabled,
      className,
      children,
      type = "button",
      ...props
    },
    ref,
  ) {
    return (
      <button
        {...props}
        ref={ref}
        type={type}
        className={cn(styles.button, className)}
        data-variant={variant}
        data-size={size}
        data-loading={loading}
        aria-busy={loading || undefined}
        disabled={disabled || loading}
      >
        {loading && (
          <LoaderCircle className={styles.spinner} aria-hidden="true" />
        )}
        {leadingIcon && (
          <span className={styles.icon} aria-hidden="true">
            {leadingIcon}
          </span>
        )}
        {children != null && <span className={styles.label}>{children}</span>}
        {trailingIcon && (
          <span className={styles.icon} aria-hidden="true">
            {trailingIcon}
          </span>
        )}
      </button>
    );
  },
);

export type ActionIconButtonProps = Omit<
  ActionButtonProps,
  "children" | "leadingIcon" | "trailingIcon" | "aria-label"
> & {
  icon: ReactNode;
  label: string;
};

export const ActionIconButton = forwardRef<
  HTMLButtonElement,
  ActionIconButtonProps
>(function ActionIconButton({ icon, label, className, ...props }, ref) {
  return (
    <ActionButton
      {...props}
      ref={ref}
      aria-label={label}
      leadingIcon={icon}
      className={cn(styles.iconButton, className)}
    />
  );
});

export type ActionLinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  variant?: ActionVariant;
  size?: "xs" | "sm" | "md" | "lg";
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  /** Render the single child (e.g. a router Link) with action styling instead of an <a>. */
  asChild?: boolean;
};

/** Navigation that looks like an action. Keeps a real link so it opens in new tabs and is announced as a link. */
export const ActionLink = forwardRef<HTMLAnchorElement, ActionLinkProps>(
  function ActionLink(
    {
      variant = "link",
      size = "sm",
      leadingIcon,
      trailingIcon,
      asChild = false,
      className,
      children,
      ...props
    },
    ref,
  ) {
    const Component = asChild ? Slot : "a";
    return (
      <Component
        {...props}
        ref={ref}
        className={cn(styles.button, className)}
        data-variant={variant}
        data-size={size}
      >
        {leadingIcon && (
          <span className={styles.icon} aria-hidden="true">
            {leadingIcon}
          </span>
        )}
        <Slottable>{children}</Slottable>
        {trailingIcon && (
          <span className={styles.icon} aria-hidden="true">
            {trailingIcon}
          </span>
        )}
      </Component>
    );
  },
);
